import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, Link2, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { CompanySearch, type CompanyOption } from '@/components/CompanySearch';
import { useCompanyContacts, useCreateTicket, useMe, usePicklists } from '@/api/hooks';
import { useRmmTicketContext } from '@/api/rmm';
import { useGapTicketCandidates, useLinkGaps, type GapGroup, type GapItem, type OpenTicket } from '@/api/insights';
import { ticketPath } from '@/lib/routes';
import { cn } from '@/lib/utils';

/**
 * Act on a gap: link the selected items to an EXISTING open ticket (offered first — Datto and CIPP
 * already raise their own tickets, and a gap may already be in hand) or create a new one. Either
 * way the server posts the gap evidence as an internal note and records the link, so the gap list
 * shows the ticket until the gap clears.
 */

const SUPPORT_QUEUE_ID = 29682833;
const STATUS_NEW = 1;
const SELECT_CLS = 'flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm text-foreground shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring';

export function GapTicketDialog({ open, onOpenChange, group, items }: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  group: GapGroup;
  /** The items to act on (a whole group, or one row). */
  items: GapItem[];
}) {
  const candidates = useGapTicketCandidates(group.companyId, open);
  // One device with an RMM agent → reuse the device page's matcher for CI + contact.
  const rmmUid = items.length === 1 ? items[0].rmmUid : null;
  const deviceCtx = useRmmTicketContext(rmmUid ?? undefined, open && !!rmmUid);
  const loading = (group.companyId != null && candidates.isLoading) || (!!rmmUid && deviceCtx.isLoading);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Ticket for this gap</DialogTitle>
          <div className="text-xs text-muted-foreground">
            {items.length === 1 ? items[0].subject : `${items.length} items`} · {group.companyName ?? 'No client'} · {group.action}
          </div>
        </DialogHeader>
        {loading ? (
          <div className="flex items-center justify-center gap-2 px-6 pb-8 pt-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Looking for open tickets…
          </div>
        ) : (
          <GapTicketBody group={group} items={items} candidates={candidates.data?.items ?? []} candidatesError={candidates.data?.error ?? (candidates.error ? 'Could not load open tickets' : null)}
            ci={deviceCtx.data?.configurationItem ?? null} deviceContactId={deviceCtx.data?.contact?.id ?? null} onDone={() => onOpenChange(false)} />
        )}
      </DialogContent>
    </Dialog>
  );
}

function GapTicketBody({ group, items, candidates, candidatesError, ci, deviceContactId, onDone }: {
  group: GapGroup;
  items: GapItem[];
  candidates: OpenTicket[];
  candidatesError: string | null;
  ci: { id: number; name: string } | null;
  deviceContactId: number | null;
  onDone: () => void;
}) {
  const subjects = items.map((i) => i.subject.toLowerCase());
  // A ticket that names one of the items (or sits on the device's CI) is probably the same work.
  const likely = (t: OpenTicket) => (ci != null && t.configurationItemID === ci.id) || subjects.some((s) => t.title.toLowerCase().includes(s.split('@')[0]));
  const ranked = useMemo(() => [...candidates].sort((a, b) => Number(likely(b)) - Number(likely(a))), [candidates]); // eslint-disable-line react-hooks/exhaustive-deps
  const [mode, setMode] = useState<'link' | 'new'>(candidates.some(likely) ? 'link' : 'new');

  return (
    <div className="px-6 pb-6">
      <div className="mb-4 flex gap-1 rounded-lg bg-muted p-1">
        {([['link', `Link existing (${candidates.length})`, Link2], ['new', 'New ticket', Plus]] as const).map(([id, label, Icon]) => (
          <button key={id} type="button" onClick={() => setMode(id)}
            className={cn('flex flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium', mode === id ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground')}>
            <Icon className="h-3.5 w-3.5" />{label}
          </button>
        ))}
      </div>
      {mode === 'link'
        ? <LinkExisting items={items} tickets={ranked} likely={likely} error={candidatesError} hasCompany={group.companyId != null} onDone={onDone} />
        : <NewTicket group={group} items={items} ci={ci} deviceContactId={deviceContactId} onDone={onDone} />}
    </div>
  );
}

function LinkExisting({ items, tickets, likely, error, hasCompany, onDone }: {
  items: GapItem[];
  tickets: OpenTicket[];
  likely: (t: OpenTicket) => boolean;
  error: string | null;
  hasCompany: boolean;
  onDone: () => void;
}) {
  const navigate = useNavigate();
  const link = useLinkGaps();
  const [ticketId, setTicketId] = useState<number | null>(tickets.find(likely)?.id ?? null);
  const [note, setNote] = useState(true);

  async function submit() {
    if (ticketId == null) return;
    try {
      const r = await link.mutateAsync({ keys: items.map((i) => i.key), ticketId, note });
      onDone();
      const action = { label: 'Open ticket', onClick: () => navigate(ticketPath(r.ticket.id)) };
      if (r.noteError) toast.warning(`Linked to ${r.ticket.ticketNumber}, but the evidence note failed`, { description: r.noteError, action });
      else toast.success(`Linked to ${r.ticket.ticketNumber}`, { action });
    } catch (err) {
      toast.error('Failed to link ticket', { description: err instanceof Error ? err.message : 'Unknown error' });
    }
  }

  if (!hasCompany) return <p className="py-6 text-center text-sm text-muted-foreground">This gap isn't mapped to a client, so there are no open tickets to offer. Create a new ticket instead.</p>;
  if (error) return <p className="py-6 text-center text-sm text-destructive">{error}</p>;
  if (!tickets.length) return <p className="py-6 text-center text-sm text-muted-foreground">The client has no open tickets. Create a new one.</p>;

  return (
    <div className="space-y-4">
      <div className="max-h-80 overflow-y-auto rounded-md border border-border">
        {tickets.map((t) => (
          <label key={t.id} className={cn('flex cursor-pointer items-start gap-3 border-b border-divider px-3 py-2 text-sm last:border-b-0 hover:bg-muted/40', ticketId === t.id && 'bg-muted/60')}>
            <input type="radio" name="gap-ticket" className="mt-1 h-3.5 w-3.5 accent-primary" checked={ticketId === t.id} onChange={() => setTicketId(t.id)} />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="font-mono text-xs text-muted-foreground">{t.ticketNumber}</span>
                {likely(t) && <span className="rounded-full bg-primary/10 px-1.5 py-px text-[10px] font-semibold text-primary">Likely match</span>}
              </div>
              <div className="truncate text-foreground" title={t.title}>{t.title}</div>
              <div className="text-[11px] text-muted-foreground">{t.statusLabel} · {t.priorityLabel} · opened {new Date(t.createDate).toLocaleDateString()}</div>
            </div>
          </label>
        ))}
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-divider pt-4">
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={note} onChange={(e) => setNote(e.target.checked)} className="h-4 w-4" />
          Add the gap details as an internal note
        </label>
        <div className="flex items-center gap-3">
          <Button type="button" variant="ghost" onClick={onDone} disabled={link.isPending}>Cancel</Button>
          <Button type="button" onClick={submit} disabled={ticketId == null || link.isPending}>
            {link.isPending && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}Link ticket
          </Button>
        </div>
      </div>
    </div>
  );
}

function NewTicket({ group, items, ci, deviceContactId, onDone }: {
  group: GapGroup;
  items: GapItem[];
  ci: { id: number; name: string } | null;
  deviceContactId: number | null;
  onDone: () => void;
}) {
  const navigate = useNavigate();
  const picklists = usePicklists();
  const { data: me } = useMe();
  const createTicket = useCreateTicket();
  const link = useLinkGaps();

  const initialCompany = group.companyId != null ? { id: group.companyId, name: group.companyName ?? `#${group.companyId}` } : null;
  const [company, setCompany] = useState<CompanyOption | null>(initialCompany);
  const [contactId, setContactId] = useState(deviceContactId != null ? String(deviceContactId) : '');
  const [linkCi, setLinkCi] = useState(!!ci);
  const [title, setTitle] = useState(`${group.action}: ${items.length === 1 ? items[0].subject : `${items.length} ${group.kind === 'no-backup' || group.kind === 'leaver' ? 'users' : 'devices'}`}`);
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('');
  const [queueId, setQueueId] = useState(String(SUPPORT_QUEUE_ID));
  const [assignToMe, setAssignToMe] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const contacts = useCompanyContacts(company?.id ?? null);
  const pl = picklists.data;
  const byLabel = (list: { value: string; label: string }[] | undefined, label: string) => list?.find((o) => o.label.toLowerCase() === label)?.value;
  const effectivePriority = priority || byLabel(pl?.priority, 'medium') || '';
  const ticketTypeId = byLabel(pl?.ticketType, 'service request');
  const sameCompany = company?.id === group.companyId;

  // One user gap → preselect the contact whose email is that user (once contacts load).
  const userEmail = items.length === 1 && items[0].subject.includes('@') ? items[0].subject.toLowerCase() : null;
  const emailMatch = userEmail && sameCompany ? contacts.data?.items.find((c) => c.emailAddress?.toLowerCase() === userEmail) : undefined;
  const effectiveContact = contactId || (emailMatch ? String(emailMatch.id) : '');

  const canSubmit = !!title.trim() && !!company && !!effectivePriority && !submitting;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit || !company) return;
    setSubmitting(true);
    const fields: Record<string, unknown> = {
      title: title.trim(), companyID: company.id, status: STATUS_NEW, priority: Number(effectivePriority), queueID: Number(queueId),
    };
    if (description.trim()) fields.description = description.trim();
    if (effectiveContact) fields.contactID = Number(effectiveContact);
    if (ci && linkCi && sameCompany) fields.configurationItemID = ci.id;
    if (ticketTypeId) fields.ticketType = Number(ticketTypeId);
    if (assignToMe && me?.item?.id) fields.assignedResourceID = me.item.id;

    let id: number | undefined;
    try {
      const data = await createTicket.mutateAsync(fields);
      id = (data as any)?.itemId ?? (data as any)?.item?.id ?? (data as any)?.id; // Autotask returns { itemId }
    } catch (err) {
      setSubmitting(false);
      toast.error('Failed to create ticket', { description: err instanceof Error ? err.message : 'Unknown error' });
      return;
    }
    if (id == null) {
      setSubmitting(false);
      toast.warning('Ticket created, but Autotask returned no id — link it from "Link existing"');
      return;
    }

    // The ticket is what must land; linking + the evidence note are best-effort after it.
    const action = { label: 'Open ticket', onClick: () => navigate(ticketPath(id!)) };
    try {
      const r = await link.mutateAsync({ keys: items.map((i) => i.key), ticketId: id, note: true });
      if (r.noteError) toast.warning(`Ticket ${r.ticket.ticketNumber} created, but the evidence note failed`, { description: r.noteError, action });
      else toast.success(`Ticket ${r.ticket.ticketNumber} created and linked`, { action });
    } catch {
      toast.warning('Ticket created, but linking it to the gap failed', { description: 'Use "Link existing" to attach it.', action });
    }
    setSubmitting(false);
    onDone();
  }

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Row label="Company" required>
          <CompanySearch value={company} onChange={(c) => { setCompany(c); setContactId(''); }} />
          {!initialCompany && <p className="mt-1 text-xs text-warning">This gap isn't mapped to a client — pick one.</p>}
        </Row>
        <Row label="Contact">
          <select value={effectiveContact} onChange={(e) => setContactId(e.target.value)} className={SELECT_CLS} disabled={!company || contacts.isLoading}>
            <option value="">{contacts.isLoading ? 'Loading contacts…' : '— No contact —'}</option>
            {contacts.data?.items.map((c) => <option key={c.id} value={c.id}>{`${c.firstName ?? ''} ${c.lastName ?? ''}`.trim()}</option>)}
          </select>
          {emailMatch && effectiveContact === String(emailMatch.id) && <p className="mt-1 text-xs text-muted-foreground">Matched by email</p>}
        </Row>
      </div>

      {ci && sameCompany && (
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={linkCi} onChange={(e) => setLinkCi(e.target.checked)} className="h-4 w-4" />
          Link to configuration item <span className="font-medium">{ci.name}</span><span className="text-xs text-muted-foreground">(CI #{ci.id})</span>
        </label>
      )}

      <Row label="Title" required>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} autoFocus />
      </Row>
      <Row label="Description">
        <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} placeholder="Optional (can be visible to the customer)" />
        <p className="mt-1 text-xs text-muted-foreground">The gap details ({items.length === 1 ? '1 item' : `${items.length} items`}) are added as an internal note.</p>
      </Row>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Row label="Priority" required>
          <select value={effectivePriority} onChange={(e) => setPriority(e.target.value)} className={SELECT_CLS}>
            {pl?.priority.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </Row>
        <Row label="Queue">
          <select value={queueId} onChange={(e) => setQueueId(e.target.value)} className={SELECT_CLS}>
            {(pl?.queueID ?? [{ value: String(SUPPORT_QUEUE_ID), label: 'Support' }]).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </Row>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-divider pt-4">
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={assignToMe} onChange={(e) => setAssignToMe(e.target.checked)} className="h-4 w-4" />
          Assign to me
        </label>
        <div className="flex items-center gap-3">
          <span className="text-xs text-muted-foreground">{ticketTypeId ? 'Service Request · ' : ''}New</span>
          <Button type="button" variant="ghost" onClick={onDone} disabled={submitting}>Cancel</Button>
          <Button type="submit" disabled={!canSubmit}>
            {submitting && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}Create ticket
          </Button>
        </div>
      </div>
    </form>
  );
}

function Row({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <div className="mb-1.5 text-xs font-medium text-muted-foreground">{label}{required && <span className="ml-0.5 text-destructive">*</span>}</div>
      {children}
    </div>
  );
}
